import { Fragment, ReactElement, useState } from "react";
import {
  Button,
  DialogTitle,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip, TextField
} from "@mui/material";
import { ITask, ITaskComment } from "../types";
import { Add, Delete } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { BaseType, IColumn, IUser } from "../../../../types";
import StyledTableRow from "../../../../components/styledTableRow";
import InputDialog from "../../../../components/dialogs/inputDialog";
import { DateTime } from "luxon";
import EditIcon from "@mui/icons-material/Edit";

interface IProps {
  user: IUser;
  task: ITask;
  updateTask: (task: ITask) => void;
  onClose: () => void;
  isManager: boolean;
}

const columns: IColumn[] = [

  { id: "datetime", label: "Created", minWidth: 100 },
  { id: "userName", label: "Owner", minWidth: 200 },
  { id: "comment", label: "Comment", minWidth: 300 },
  { id: "actions", label: "Actions", minWidth: 100 },

];


const TaskComments = ({ task, updateTask, onClose, user, isManager }: IProps): ReactElement => {
  const [comments, setComments] = useState((task.extraData.comments ?? []) as ITaskComment[]);
  const [comment, setComment] = useState<ITaskComment | null>(null);
  const [commentIndex, setCommentIndex] = useState<number >(-1);


  const onInternalAccept = () => {
    task.extraData.comments = comments;
    updateTask(task);
  };

  const onDeleteComment = (index: number) => {
    comments.splice(index, 1);
    setComments([...comments]);
  };

  const onAddComment = () => {
    setComment({ comment: "", datetime: DateTime.now().toFormat("yyyy-MM-dd"), userID: user.userID, userName: user.fullName });
    setCommentIndex(-1);
  };

  const onAcceptNewComment = () => {
    if (commentIndex === -1 && comment){
      setComments([...comments, comment]);
    } else if (comment){
      setComments(comments.map((c, i) => i === commentIndex ? comment : c));
    }

    setComment(null);
  };

  const onEditComment = (item: ITaskComment, index: number) => {
    setComment({ ...item, userID: user.userID, userName: user.fullName });
    setCommentIndex(index);
  };

  const onCancelNewComment = () => {
    setComment(null);
  };

  const onCommentChange = (text: string) => {
    if (comment){
      setComment({ ...comment, comment: text });
    }
  };

  return (
    <Dialog open={true} maxWidth={"lg"} keepMounted={true}>
      <DialogTitle>Comments</DialogTitle>

      <DialogContent dividers={true} style={{ width: 800 }} className="p-2">
        <div>
          <Button onClick={onAddComment} variant="contained" color="primary" startIcon={<Add/>} className="m-1">
            Add Comment
          </Button>
          <TableContainer>
            <Table stickyHeader={true} size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column, index) =>
                    <TableCell
                      className="table-header"
                      key={`${column.id}-${index}`}
                      align={column.align}
                      style={{ minWidth: column.minWidth, width: column.minWidth }}>
                      {column.id !== "actions" ? column.label : <Fragment/>}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {comments.map((row: ITaskComment, index: number) => {
                  return (
                    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`prod-eaf-row-${index}`}>
                      {columns.map((column: IColumn) => {

                        if (column.id === "actions") {
                          return (
                            <TableCell key={`${column.id}-${index}`} align="right">
                              <>
                                <Tooltip title="Edit Comment">
                                  <IconButton size={"small"} disabled={!isManager && row.userID !== user.userID}
                                    onClick={() => onEditComment(row, index)} color="info">
                                    <EditIcon/>
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Comment">
                                  <IconButton size={"small"} disabled={!isManager && row.userID !== user.userID}
                                    onClick={() => onDeleteComment(index)} color="error">
                                    <Delete/>
                                  </IconButton>
                                </Tooltip>
                              </>
                            </TableCell>);
                        }

                        const value = row[column.id as keyof ITaskComment] as BaseType;

                        return (
                          <TableCell key={`${column.id}-${index}`} align={column.align}>
                            {column.format && typeof value === "number" ? column.format(value) : value}
                          </TableCell>
                        );
                      })}
                    </StyledTableRow>
                  );
                })}

              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onInternalAccept} variant="contained" color="primary" startIcon={<SaveIcon/>}>
          Save
        </Button>
        <Button onClick={onClose} variant="contained" color="secondary" startIcon={<CloseIcon/>}>
          Close
        </Button>
      </DialogActions>
      <InputDialog open={comment !== null} title={commentIndex === -1 ? "New Comment" : "Edit Comment"} onAccept={onAcceptNewComment} onCancel={onCancelNewComment} >
        <div style={{ width: 400 }}>
          <TextField
            variant="standard"
            placeholder={"Comment"}
            label={"Comment"}
            value={comment ? comment.comment : ""}
            multiline={true}
            fullWidth={true}
            rows={5}
            onChange={event => onCommentChange(event.target.value)}/>
        </div>
      </InputDialog>
    </Dialog>
  );
};

export default TaskComments;
